import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiErrorHandler } from './useApiErrorHandler';
import { IServerSidePaginatedFetcherParams } from '../contexts/Fetcher';
import Axios, { AxiosRequestConfig } from 'axios';
import { useDebouncedCallback } from 'use-debounce';

export const useSearchableDropdown = <DataType extends unknown>(
  executeGetData: (params: IServerSidePaginatedFetcherParams, config: AxiosRequestConfig) => Promise<DataType[]>,
  initialFetch = true
) => {
  const handleError = useApiErrorHandler();
  const abortControllerRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<DataType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const onLoadData = useCallback(async (search: string) => {
    setData([]);
    setIsLoading(true);
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      const result = await executeGetData(
        {
          page: 1,
          limit: 20,
          filter: {
            filterField: 'text',
            filterValue: search,
            filterOp: 'ilike',
          },
        },
        { signal: abortControllerRef.current?.signal }
      );

      abortControllerRef.current = null;

      setData(result);
    } catch (e) {
      if (Axios.isCancel(e)) {
        return;
      }
      handleError(e);
    }
    setIsLoading(false);
  }, []);

  const debouncedOnLoadData = useDebouncedCallback(onLoadData, 500);

  useEffect(() => {
    if (initialFetch) {
      debouncedOnLoadData('');
    }
  }, [initialFetch]);

  return {
    data,
    isLoading,
    onSearch: debouncedOnLoadData,
  };
};
